import { formatLeadDisplayName } from './lead-name';

export type LeadDuplicateMatchReason = 'email' | 'phone' | 'nameCompany';

export type LeadDuplicateCandidate = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
};

export type LeadDuplicateHit<T extends LeadDuplicateCandidate> = {
  item: T;
  reasons: LeadDuplicateMatchReason[];
};

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

function normalizePhone(phone: string | null | undefined): string {
  return (phone ?? '').replace(/\D/g, '');
}

function phonesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const suffixLen = 9;
  if (na.length >= suffixLen && nb.length >= suffixLen) {
    return na.slice(-suffixLen) === nb.slice(-suffixLen);
  }
  return false;
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function matchLeadDuplicateReasons(
  candidate: LeadDuplicateCandidate,
  existing: LeadDuplicateCandidate,
): LeadDuplicateMatchReason[] {
  const reasons: LeadDuplicateMatchReason[] = [];

  const candidateEmail = normalizeEmail(candidate.email);
  const existingEmail = normalizeEmail(existing.email);
  if (candidateEmail && existingEmail && candidateEmail === existingEmail) {
    reasons.push('email');
  }

  if (phonesMatch(candidate.phone, existing.phone)) {
    reasons.push('phone');
  }

  const candidateName = normalizeText(
    formatLeadDisplayName(candidate.firstName, candidate.lastName),
  );
  const existingName = normalizeText(
    formatLeadDisplayName(existing.firstName, existing.lastName),
  );
  const candidateCompany = normalizeText(candidate.companyName);
  const existingCompany = normalizeText(existing.companyName);
  if (
    candidateName &&
    existingName &&
    candidateName === existingName &&
    candidateCompany &&
    existingCompany &&
    candidateCompany === existingCompany
  ) {
    reasons.push('nameCompany');
  }

  return reasons;
}

function reasonScore(reasons: LeadDuplicateMatchReason[]): number {
  return (
    (reasons.includes('email') ? 4 : 0) +
    (reasons.includes('phone') ? 2 : 0) +
    (reasons.includes('nameCompany') ? 1 : 0)
  );
}

/** Finds records that share contact data with the incoming candidate. */
export function findLeadDuplicateMatches<T extends LeadDuplicateCandidate>(
  existing: readonly T[],
  candidate: LeadDuplicateCandidate,
): LeadDuplicateHit<T>[] {
  const matches: LeadDuplicateHit<T>[] = [];

  for (const item of existing) {
    const reasons = matchLeadDuplicateReasons(candidate, item);
    if (reasons.length > 0) {
      matches.push({ item, reasons });
    }
  }

  return matches.sort(
    (a, b) => reasonScore(b.reasons) - reasonScore(a.reasons),
  );
}
